import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import {
  getHealth,
  recategorizeHealth,
  removeHealthItem,
  submitHealthAnswer,
  type HealthCategory,
} from "@/lib/api/endpoints/profile-interview";
import { InterviewPanel } from "@/features/interview/InterviewPanel";
import "./HealthPanel.css";

const HEALTH_SEEDS = [
  "Tell me what's going on with your health — anything current, plus history I should know.",
  "How are you doing health-wise? Conditions, meds, fitness, sleep — whatever's on your mind.",
  "Walk me through your health picture — what's current, and any history worth noting.",
  "What should I know about your health and how you're feeling lately?",
];
const pick = (a: string[]) => a[Math.floor(Math.random() * a.length)];

const SECTIONS: { key: HealthCategory; label: string }[] = [
  { key: "medications", label: "Medications" },
  { key: "conditions", label: "Conditions" },
  { key: "history", label: "History" },
  { key: "exercise", label: "Exercise" },
  { key: "diet", label: "Weight & Diet" },
];

/**
 * Health tab — captured health facts organized into logical areas (medications,
 * conditions, history, exercise, weight & diet) plus generated recommendations.
 */
export function HealthPanel() {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.synthesis.tab(ws, "health");
  const [seed, setSeed] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let d = await getHealth(token ?? null, ws);
      // One-time: facts captured before categories all sit in History — sort them.
      if (d.items.some((it) => it.id.startsWith("health-legacy-"))) {
        try {
          d = await recategorizeHealth(token ?? null, ws);
        } catch {
          /* keep the unsorted data if recategorize fails */
        }
      }
      return d;
    },
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });

  const remove = async (id: string) => {
    queryClient.setQueryData(queryKey, await removeHealthItem(token ?? null, ws, id));
  };

  const items = data?.items ?? [];
  const recLines = (data?.recommendations ?? "")
    .split("\n")
    .map((l) => l.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);

  return (
    <div className="hp-wrap">
      <div className="hp-bar">
        <button
          className="people-add-btn people-talk-btn"
          onClick={() => setSeed(pick(HEALTH_SEEDS))}
        >
          💬 Talk to Xavior about health
        </button>
      </div>

      {isLoading ? (
        <div className="hp-muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="hp-muted">
          Nothing yet — tell me what's going on with your health and I'll organize
          it.
        </div>
      ) : (
        <>
          {SECTIONS.map(({ key, label }) => {
            const rows = items.filter((it) => it.category === key);
            if (rows.length === 0) return null;
            return (
              <div key={key} className="hp-section">
                <div className="hp-section-title">{label}</div>
                {rows.map((it) => (
                  <div key={it.id} className="hp-item">
                    <span className="hp-item-check">✓</span>
                    <span className="hp-item-text">{it.text}</span>
                    <button
                      className="hp-item-del"
                      aria-label="Delete"
                      onClick={() => void remove(it.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            );
          })}

          {recLines.length > 0 && (
            <div className="hp-section hp-recs">
              <div className="hp-section-title">Recommendations</div>
              {recLines.map((l, i) =>
                /not medical advice/i.test(l) ? (
                  <div key={i} className="hp-disclaimer">
                    {l}
                  </div>
                ) : (
                  <div key={i} className="hp-rec">
                    <span className="hp-rec-dot">›</span> {l}
                  </div>
                ),
              )}
            </div>
          )}
        </>
      )}

      {seed && (
        <InterviewPanel
          seedQuestion={seed}
          title="Talk to Xavior about health"
          onAnswer={submitHealthAnswer}
          continuous
          onClose={() => setSeed(null)}
          onComplete={() => {
            void queryClient.invalidateQueries({ queryKey });
          }}
        />
      )}
    </div>
  );
}

export default HealthPanel;
